import {
    ClinicianAppointmentOverrideInput,
    VisitAssignmentInput,
    VisitAssignmentVisitInput,
} from '@/types/graphql';

/**
 * Builds the `VisitAssignmentInput` for the `decisionSupport.visitAssignment`
 * queries from plain per-visit selection state.
 *
 * Decoupled from the reassignment v3 `Decision` union on purpose: the assignment
 * surfaces hold much simpler state (a chosen clinician per appointment, an
 * optional chosen/held date per visit), so they don't need the full
 * reassign/remove/leave-as-is taxonomy.
 *
 * Tri-state semantics (see the porting guide):
 *  - clinician: each appointment passed becomes an override entry. `clinicianId`
 *    holds the chosen/current clinician; `null` clears it. Omit an appointment
 *    from `appointments` to send "no proposal" for it.
 *  - date: `dateOverride` is tri-state — `undefined` = no proposal, `null` =
 *    explicit clear, a string = set/keep that date.
 */
export type VisitAppointmentOverrideState = {
    /** clinician_appointment id */
    id: string;
    /** chosen/current clinician for this appointment; null clears it */
    clinicianId: string | null;
};

export type VisitOverrideState = {
    visitId: string;
    appointments: VisitAppointmentOverrideState[];
    /** ISO `YYYY-MM-DD`; `undefined` = no proposal, `null` = cleared */
    dateOverride?: string | null;
};

const buildClinicianOverrides = (
    appointments: VisitAppointmentOverrideState[],
): ClinicianAppointmentOverrideInput[] =>
    appointments.map((appt) => ({
        clinicianAppointmentId: appt.id,
        clinicianOverride: { clinicianId: appt.clinicianId },
    }));

const buildDateOverride = (
    dateOverride: string | null | undefined,
): VisitAssignmentVisitInput['dateOverride'] => {
    // No proposal. We send an explicit `null` wrapper rather than omitting the
    // key: the BE input class doesn't default `dateOverride` to None.
    if (dateOverride === undefined) {
        return null;
    }
    // Explicit clear vs set/keep.
    return { date: dateOverride };
};

const buildVisitInput = (visit: VisitOverrideState): VisitAssignmentVisitInput => ({
    visitId: visit.visitId,
    dateOverride: buildDateOverride(visit.dateOverride),
    clinicianAppointmentOverrides: buildClinicianOverrides(visit.appointments),
});

export const buildVisitAssignmentInput = (visits: VisitOverrideState[]): VisitAssignmentInput => ({
    // The visits array is a queue: the engine walks it in order and visit i's
    // recommendations account for the tentative state of visits 0..i-1. Send
    // visits in display order.
    visits: visits.map(buildVisitInput),
});
